//@ts-nocheck
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    useTable,
    useSortBy,
    usePagination,
    useRowSelect,
    Column,
    useExpanded,
    useRowState,
    useResizeColumns,
} from 'react-table';
import { FormattedMessage } from 'react-intl';
import Pagination from './Pagination';
import ServerSidePagination from './ServerSidePagination';
import { StyledTable } from './StyledTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { ConditionalRendering } from '../conditional-rendering';

export const IndeterminateCheckbox = forwardRef(
    ({ indeterminate, isDisabled, isChecked, ...rest }: any, ref: React.ForwardedRef<HTMLInputElement>) => {
        const defaultRef = useRef<HTMLInputElement>(null);
        const resolvedRef: any = ref || defaultRef;

        useEffect(() => {
            if (resolvedRef && resolvedRef.current) {
                resolvedRef.current.indeterminate = indeterminate;
            }
        }, [resolvedRef, indeterminate]);

        return <input type="checkbox" ref={resolvedRef} disabled={isDisabled} checked={isChecked} {...rest} />;
    },
);

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

interface TableProps<T extends object> {
    id: string;
    className?: string;
    variant?: 'simple' | 'striped' | 'unstyled' | 'dense';
    data: T[];
    columns: Column<T>[];
    isHeadless?: boolean;
    allowSort?: boolean;
    useControlledState?: Function;
    hasAccordion?: boolean;
    defaultExpanded?: { [index: string]: boolean };
    hasExpandedRow?: boolean;
    renderRowSubComponent?: (props: { row: any }) => React.ReactNode;
    hasPagination?: boolean;
    isClientPagination?: boolean;
    onPagination?: (data: any) => void;
    totalCount?: number;
    rowsPerPage?: number;
    visiblePageNumbers?: number;
    clientPageSize?: number;
    isShowPaginationHeader?: boolean;
    paginationPrevText?: string;
    paginationNextText?: string;
    isShowRowsPerPage?: boolean;
    maxNumPages?: number;
    rowsPerPageOptions?: number[];
    isFirstAndLastNavigation?: boolean;
    hasLastRowBorderBtm?: boolean;
    width?: string | number;
    sortById?: string;
    sortDesc?: boolean;
    disableSortRemove?: boolean;
    disableDefaultSortBy?: boolean;
    manualSortBy?: boolean;
    onSortChange?: (data: any) => void;
    onRowSelect?: (selectedRowIds: any[]) => void;
    rowClickCallback?: (data: any) => void;
    selectedPage?: number;
    cellFontSize?: string | number;
    headerFontSize?: string | number;
    hasBorders?: boolean;
    selectionKey?: string;
}

const Table = forwardRef(
    <T extends object>(
        {
            className,
            allowSort,
            columns,
            data,
            defaultExpanded,
            hasAccordion,
            hasExpandedRow,
            hasPagination,
            hasLastRowBorderBtm,
            id,
            isHeadless = false,
            sortById,
            sortDesc,
            useControlledState,
            disableSortRemove = false,
            isClientPagination = false,
            onPagination,
            onSortChange,
            totalCount,
            selectedPage = 1,
            rowsPerPage = 10,
            visiblePageNumbers = 3,
            onRowSelect,
            rowClickCallback,
            clientPageSize = 10,
            isShowPaginationHeader = true,
            isShowRowsPerPage = false,
            paginationPrevText,
            paginationNextText,
            renderRowSubComponent,
            maxNumPages,
            rowsPerPageOptions,
            variant, // the variant prop to control table styles
            isFirstAndLastNavigation,
            disableDefaultSortBy,
            manualSortBy = false,
            cellFontSize = '13px',
            headerFontSize = '14px',
            hasBorders = false,
            selectionKey = 'id',
        }: TableProps<T>,
        ref: React.Ref<unknown> | undefined,
    ) => {
        const extraHooks: any[] = [];
        if (allowSort) {
            extraHooks.push(useSortBy);
        }
        if (hasAccordion || hasExpandedRow) {
            extraHooks.push(useExpanded);
        }
        if (hasPagination) {
            extraHooks.push(usePagination);
        }

        const [pageNumbersToShow, setPageNumbersToShow] = useState<number[]>([]);
        const [currentPage, setCurrentPage] = useState(0);

        const {
            canNextPage,
            canPreviousPage,
            getTableBodyProps,
            getTableProps,
            gotoPage,
            headerGroups,
            nextPage,
            page,
            pageCount,
            pageOptions,
            prepareRow,
            previousPage,
            rows,
            setPageSize,
            state,
            rowHoverProps,
        }: any = useTable(
            {
                id,
                columns,
                data,
                disableSortRemove,
                initialState: {
                    pageIndex: currentPage,
                    expanded: defaultExpanded || {},
                    rowState: {},
                    pageSize: clientPageSize,
                    sortBy: disableDefaultSortBy
                        ? []
                        : [
                              {
                                  id: sortById ? sortById : 'stdName',
                                  desc: sortDesc ? sortDesc : false,
                              },
                          ],
                },
                useControlledState: useControlledState,
                manualPagination: !isClientPagination,
                autoResetSortBy: false,
                manualSortBy,
                getRowId: (row: any) => row[selectionKey],
            },
            useRowState,
            ...extraHooks,
            useRowSelect,
            useResizeColumns,
        );

        useEffect(() => {
            if (allowSort && onSortChange) {
                onSortChange(state.sortBy);
            }
        }, [state.sortBy]);

        useEffect(() => {
            const selectedRowIds = state.selectedRowIds;
            const selectedRows = data.filter(row => selectedRowIds[row[selectionKey]]);
            onRowSelect?.(selectedRows);
        }, [state.selectedRowIds]);

        useImperativeHandle(ref, () => ({
            navigateToPage(pageNumber: number) {
                setCurrentPage(pageNumber);
                gotoPage(pageNumber + 1);
            },
        }));

        const { pageIndex, pageSize, disablePaginationAndSorting } = state;

        const setVisiblePages = (pages: number[]) => {
            setPageNumbersToShow(pages);
        };

        const getVisiblePages = (currentPage: number, totalPages: number, visiblePages: number) => {
            const pages = [];
            const half = Math.floor(visiblePages / 2);

            let start = Math.max(1, currentPage - half);
            let end = Math.min(totalPages, currentPage + half);

            if (currentPage <= half) {
                end = Math.min(totalPages, visiblePages);
            } else if (currentPage + half >= totalPages) {
                start = Math.max(1, totalPages - visiblePages + 1);
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        };

        const onServerPagination = (nextPage: number, pageSize: number) => {
            onPagination({ page: nextPage, pageSize });
            const pageCount = Math.ceil(totalCount / pageSize);
            const nums = getVisiblePages(nextPage, pageCount, visiblePageNumbers);
            setPageNumbersToShow(nums);
        };

        return (
            <div className={className} id={id} data-automation-id={id} data-testid={id}>
                <StyledTable
                    variant={variant}
                    headerFontSize={headerFontSize}
                    cellFontSize={cellFontSize}
                    hasBorders={hasBorders}
                    {...getTableProps()}
                >
                    {!isHeadless && (
                        <thead>
                            {headerGroups.map((headerGroup: any, groupIndex: number) => (
                                <tr key={groupIndex} {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column: any, columnIndex: number) => {
                                        const isSortEnabled = column.isSortEnabled ?? true;

                                        const headerProps = {
                                            ...column.getHeaderProps({
                                                ...(allowSort && isSortEnabled && !disablePaginationAndSorting
                                                    ? column.getSortByToggleProps()
                                                    : {}),
                                                style: {
                                                    cursor: allowSort && isSortEnabled ? 'pointer' : '',
                                                    width: column.width,
                                                    minWidth: column.minWidth,
                                                    verticalAlign: column.verticalAlign,
                                                    className: column.className,
                                                    ...(column.textTransform
                                                        ? { textTransform: column.textTransform }
                                                        : {}),
                                                    borderRight:
                                                        headerGroups[headerGroups.length - 1].headers[columnIndex]
                                                            .borderRight || 'none',
                                                },
                                            }),
                                        };

                                        return (
                                            <th
                                                key={columnIndex}
                                                data-testid={`table-heading-${columnIndex}`}
                                                {...headerProps}
                                            >
                                                <div>
                                                    <span style={{ marginRight: '4px' }}>
                                                        {typeof column.Header === 'string' ? (
                                                            <FormattedMessage id={column.Header} />
                                                        ) : (
                                                            column.render('Header')
                                                        )}
                                                    </span>
                                                    {column.isResizable && (
                                                        <div
                                                            {...column.getResizerProps()}
                                                            style={{
                                                                display: 'inline-block',
                                                                background: 'transparent',
                                                                width: '5px',
                                                                height: '100%',
                                                                cursor: 'col-resize',
                                                                position: 'absolute',
                                                                right: 0,
                                                                top: 0,
                                                            }}
                                                        />
                                                    )}
                                                    {allowSort && column.isSorted && (
                                                        <>
                                                            {column.isSortedDesc ? (
                                                                <FontAwesomeIcon icon={faArrowDown} size="sm" />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faArrowUp} size="sm" />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                    )}
                    <tbody {...getTableBodyProps()}>
                        {(hasPagination ? page : rows)?.map((row: any, rowIndx: number) => {
                            prepareRow(row);
                            const lastRow = rowIndx === rows.length - 1;
                            if (row.canExpand) {
                                if (hasAccordion) {
                                    return (
                                        <tr
                                            key={rowIndx}
                                            {...row.getRowProps()}
                                            style={{
                                                borderBottom: !hasLastRowBorderBtm && lastRow ? 'none' : undefined,
                                            }}
                                        >
                                            <td colSpan={row.cells.length} data-testid={`table-row-${rowIndx}`}>
                                                <div {...row.getToggleRowExpandedProps()}>
                                                    <span>{row.cells[0].render('Cell')}</span>
                                                    <span>
                                                        {row.isExpanded ? (
                                                            <FontAwesomeIcon icon={faChevronUp} />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faChevronDown} />
                                                        )}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                } else {
                                    return (
                                        <tr
                                            key={rowIndx}
                                            {...row.getRowProps()}
                                            style={{
                                                borderBottom: !hasLastRowBorderBtm && lastRow ? 'none' : undefined,
                                            }}
                                        >
                                            {row.cells.map((cell: any, i: number) => (
                                                <td
                                                    key={i}
                                                    data-testid={`table-row-${rowIndx}-cell-${i}`}
                                                    {...cell.getCellProps({
                                                        style: {
                                                            textAlign:
                                                                headerGroups[headerGroups.length - 1].headers[i]
                                                                    .justify || 'start',
                                                            verticalAlign:
                                                                headerGroups[headerGroups.length - 1].headers[i]
                                                                    .align || 'center',
                                                        },
                                                    })}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                }
                            } else {
                                return (
                                    <Fragment key={rowIndx}>
                                        <tr
                                            {...row.getRowProps()}
                                            {...rowHoverProps}
                                            onClick={() => rowClickCallback?.(row.original)}
                                            style={{
                                                cursor: rowClickCallback ? 'pointer' : 'inherit',
                                                borderBottom:
                                                    renderRowSubComponent || (!hasLastRowBorderBtm && lastRow)
                                                        ? 'none'
                                                        : undefined,
                                            }}
                                        >
                                            {row.cells.map((cell: any, i: number) => (
                                                <td
                                                    key={i}
                                                    data-testid={`table-row-${rowIndx}-cell-${i}`}
                                                    {...cell.getCellProps({
                                                        style: {
                                                            paddingLeft:
                                                                i === 0 &&
                                                                row.depth > 0 &&
                                                                (hasAccordion || hasExpandedRow)
                                                                    ? `${row.depth * 4}rem`
                                                                    : undefined,
                                                            verticalAlign:
                                                                headerGroups[headerGroups.length - 1].headers[i]
                                                                    .align || 'center',
                                                            textAlign:
                                                                headerGroups[headerGroups.length - 1].headers[i]
                                                                    .textDataAlign ||
                                                                headerGroups[headerGroups.length - 1].headers[i]
                                                                    .justify ||
                                                                'start',
                                                        },
                                                    })}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            ))}
                                        </tr>
                                        {renderRowSubComponent && (
                                            <tr>
                                                <td colSpan={columns.length}>{renderRowSubComponent({ row })}</td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            }
                        })}
                    </tbody>
                </StyledTable>
                <ConditionalRendering isTrue={hasPagination && totalCount > rowsPerPage}>
                    <ConditionalRendering isTrue={isClientPagination}>
                        <div style={{ marginTop: '4px' }}>
                            <Pagination
                                {...{
                                    canPreviousPage,
                                    canNextPage,
                                    pageOptions,
                                    pageCount,
                                    gotoPage,
                                    nextPage,
                                    previousPage,
                                    setPageSize,
                                    pageIndex,
                                    pageSize,
                                    disablePagination: disablePaginationAndSorting,
                                    rows: rows,
                                    isShowPaginationHeader,
                                    isShowRowsPerPage,
                                    prevText: paginationPrevText,
                                    nextText: paginationNextText,
                                    maxNumPages,
                                    rowsPerPageOptions,
                                }}
                            />
                        </div>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!isClientPagination}>
                        <div style={{ marginTop: '4px' }}>
                            <ServerSidePagination
                                {...{
                                    totalCount,
                                    onPagination: onServerPagination,
                                    selectedPage,
                                    rowsPerPage,
                                    visiblePageNumbers,
                                    isShowPaginationHeader,
                                    setVisiblePages,
                                    pageNumbersToShow,
                                    isShowRowsPerPage,
                                    prevText: paginationPrevText,
                                    nextText: paginationNextText,
                                    rowsPerPageOptions,
                                    isFirstAndLastNavigation,
                                }}
                            />
                        </div>
                    </ConditionalRendering>
                </ConditionalRendering>
            </div>
        );
    },
);

Table.displayName = 'Table';

export default Table;
